import cloneDeep from 'lodash/cloneDeep';

import { AuthAction, AuthState } from './Auth.interface';
import { IMPERSONATE_LAST_PAGE, STAFF_EMAIL, STAFF_ID, STAFF_TOKEN_WHILE_IMPERSONATE } from './constants';

import User from 'modules/Auth/common/User.interface';
import Token from 'modules/Auth/common/Token.interface';
import { buildInitialState, setLocalStorage, setDebugUsers } from 'modules/Auth/AuthProvider/utils';
import { clearDismissalFlags } from 'modules/common/dismissalFlags';
import { clearAuth0Session } from 'modules/Auth/auth0/session';

export const initialState = buildInitialState();

export const reducer = (state: AuthState, action: AuthAction): AuthState => {
    const objectPayload = typeof action.payload === 'object' && action.payload !== null ? action.payload : {};
    let user: User | null = { ...state.user } as User;
    let token: Token | null = { ...state.token } as Token;
    let newState;

    const isImpersonating = Boolean(localStorage.getItem(STAFF_EMAIL));

    switch (action.type) {
        case 'SET_TOKEN':
            token = { ...token, ...objectPayload } as Token;
            newState = { ...state, token };
            break;
        case 'SET_USER_DATA':
            user = { ...user, ...objectPayload } as User;
            newState = { ...state, user };
            break;
        case 'SET_EXTEND_SESSION_MODAL_VISIBLE':
            newState = { ...state, showTimeoutModal: action.payload === true };
            break;
        case 'SET_AUTH0_SESSION':
            newState = { ...state, auth0Session: action.payload === true };
            break;
        // The Django session alone, leaving an Auth0 session and its markers in place.
        case 'CLEAR_DJANGO_SESSION':
            user = null;
            token = null;
            newState = { ...state, user, token };
            break;
        case 'CLEAR_SESSION':
            user = null;
            token = null;
            newState = { ...initialState, user, token };
            setLocalStorage(STAFF_EMAIL, null);
            setLocalStorage(STAFF_ID, null);
            setLocalStorage(STAFF_TOKEN_WHILE_IMPERSONATE, null);
            setLocalStorage(IMPERSONATE_LAST_PAGE, null);
            clearAuth0Session();
            // UI dismissal flags live in localStorage and nothing else expires them, so they have
            // to be dropped here or the next login on this browser inherits them.
            clearDismissalFlags();
            break;
        default:
            newState = cloneDeep(state);
    }
    setLocalStorage('token', !!token && Object.keys(token).length > 0 ? token : null);
    setLocalStorage('user', user);
    setLocalStorage('email', user?.email || null);
    setLocalStorage('employee', isImpersonating ? false : user?.is_employee);
    setLocalStorage('impersonator', isImpersonating ? false : Boolean(user?.groups?.includes('Impersonators')));
    // An Auth0 session is signed in without a Django token.
    setLocalStorage('loggedin', Boolean(token?.access) || newState.auth0Session);
    // set groups for use by useAccessGroups hook
    if ((user?.groups?.length ?? 0) > 0) {
        setLocalStorage('groups', JSON.stringify(user?.groups));
    } else {
        setLocalStorage('groups', null);
    }

    if (user?.pk) {
        setDebugUsers(user);
    }

    return newState;
};
